import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import { data, redirect, Form, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { TrialInviteBanner } from '#app/components/trial-invite-banner.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getUserId, requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
	inspectOrgInvite,
	type OrgInviteInspection,
} from '#app/utils/invite.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { RoleLabelsInline } from '#app/utils/roles.ts'
import {
	inspectTrialInvite,
	type TrialInviteInspection,
} from '#app/utils/trial-invite.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { AuthCard, AuthInlineLink, AuthSplit } from './+shared/auth-shell.tsx'
import { type Route } from './+types/signup.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z.object({
	email: EmailSchema,
	redirectTo: z.string().optional(),
	trial: z.string().optional(),
	invite: z.string().optional(),
})

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const trial = url.searchParams.get('trial')
	// A logged-in recipient cannot sign up again, and requireAnonymous would
	// drop them on /app with the token silently lost. The trial always lands
	// on a fresh organization, so hand the token to the new-org page instead.
	if (trial && (await getUserId(request))) {
		throw redirect(newOrganizationTrialPath(trial))
	}
	await requireAnonymous(request)
	let trialInspection: TrialInviteInspection | null = null
	if (trial) {
		trialInspection = await inspectTrialInvite(trial)
	}
	const invite = url.searchParams.get('invite')
	let inviteInspection: OrgInviteInspection | null = null
	if (invite) {
		inviteInspection = await inspectOrgInvite(invite)
	}
	return { trialInspection, inviteInspection }
}

function newOrganizationTrialPath(trial: string) {
	return `/app/organizations/new?trial=${encodeURIComponent(trial)}`
}

/**
 * An existing user who follows a trial link while logged out logs in from
 * here; send them on to the new-org page so the token survives the detour.
 */
function loginPath({
	redirectTo,
	trial,
}: {
	redirectTo: string | null
	trial: string | null
}) {
	const target = trial ? newOrganizationTrialPath(trial) : redirectTo
	return target ? `/login?redirectTo=${encodeURIComponent(target)}` : '/login'
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
			// An invite is bound to one address. Catching a mismatch here beats
			// letting the user through three more screens before the accept
			// step rejects them.
			if (data.invite) {
				const inspection = await inspectOrgInvite(data.invite)
				if (inspection.status === 'usable' && inspection.email !== data.email) {
					ctx.addIssue({
						path: ['email'],
						code: z.ZodIssueCode.custom,
						message: `Cette invitation a été envoyée à ${inspection.email}.`,
					})
				}
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email, redirectTo: formRedirectTo, trial, invite } = submission.value
	// Attach the trial and org-invite tokens to the redirectTo so the verify
	// handler can move them into the verify session. Defaults to /onboarding so
	// we never lose a token when no redirectTo was set.
	let effectiveRedirectTo = formRedirectTo
	if (trial || invite) {
		const base = new URL(
			effectiveRedirectTo ?? '/onboarding',
			'http://placeholder',
		)
		if (trial) base.searchParams.set('trial', trial)
		if (invite) base.searchParams.set('invite', invite)
		effectiveRedirectTo = base.pathname + base.search + base.hash
	}
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
		redirectTo: effectiveRedirectTo,
	})

	const response = await sendEmail({
		to: email,
		subject: `Bienvenue sur Vigilio !`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Bienvenue sur Vigilio !</E.Text>
				</h1>
				<p>
					<E.Text>
						Voici votre code de vérification : <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text> Ou cliquez sur le lien :</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Inscription | Vigilio' }]
}

export default function SignupRoute({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const trial = searchParams.get('trial')
	const trialInspection = loaderData?.trialInspection ?? null
	const invite = searchParams.get('invite')
	const inviteInspection = loaderData?.inviteInspection ?? null
	const lockedEmail =
		inviteInspection?.status === 'usable' ? inviteInspection.email : null

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		defaultValue: {
			redirectTo,
			trial,
			invite,
			...(lockedEmail ? { email: lockedEmail } : {}),
		},
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthSplit
			aside={{
				eyebrow: 'Inscription',
				headline: 'Quelques minutes',
				headlineItalic: 'pour commencer.',
				body: 'Créez votre compte, importez vos sources, recevez l’essentiel par courriel à la fréquence de votre choix.',
				bullets: [
					'Essai sans carte de crédit',
					'Configuration en 5 minutes',
					'Import CSV des destinataires',
				],
			}}
		>
			<AuthCard
				eyebrow="Créer un compte"
				title="Bienvenue sur"
				titleItalic="Vigilio."
				subtitle="Entrez votre courriel — nous vous enverrons un code de vérification."
				footer={
					<span className="inline-flex items-center justify-center gap-2">
						<span>Vous avez déjà un compte ?</span>
						<AuthInlineLink to={loginPath({ redirectTo, trial })}>
							Se connecter
						</AuthInlineLink>
					</span>
				}
			>
				{inviteInspection ? (
					<OrgInviteBanner inspection={inviteInspection} />
				) : null}

				{trialInspection ? (
					<TrialInviteBanner inspection={trialInspection} target="account" />
				) : null}

				<Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.email.id,
							children: 'Courriel',
						}}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
							autoFocus: true,
							autoComplete: 'email',
							placeholder: 'vous@entreprise.com',
							// readOnly, never disabled: a disabled input is left
							// out of the FormData and the action would reject
							// the submission for a missing email.
							...(lockedEmail
								? {
										readOnly: true,
										'aria-readonly': true,
										className: 'bg-slate-50 text-slate-600',
									}
								: {}),
						}}
						errors={fields.email.errors}
					/>
					<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
					<input {...getInputProps(fields.trial, { type: 'hidden' })} />
					<input {...getInputProps(fields.invite, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="mt-2 w-full"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Continuer
					</StatusButton>
				</Form>

				<p
					className="mt-6 text-center"
					style={{
						fontSize: 12,
						color: 'var(--text-secondary)',
						lineHeight: 1.5,
					}}
				>
					En continuant, vous acceptez nos{' '}
					<a
						href="/terms"
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: 'var(--brand)' }}
						className="font-medium"
					>
						conditions d'utilisation
					</a>{' '}
					et notre{' '}
					<a
						href="/privacy"
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: 'var(--brand)' }}
						className="font-medium"
					>
						politique de confidentialité
					</a>
					.
				</p>
			</AuthCard>
		</AuthSplit>
	)
}

function OrgInviteBanner({ inspection }: { inspection: OrgInviteInspection }) {
	if (inspection.status === 'usable') {
		return (
			<div
				className="mb-6 rounded-lg p-3.5 text-sm"
				style={{
					background: 'var(--brand-soft)',
					border: '1px solid rgba(93, 47, 240, 0.18)',
					color: 'var(--ink)',
				}}
			>
				<span
					className="mb-1.5 block uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 10.5,
						letterSpacing: '0.14em',
						color: 'var(--brand)',
						fontWeight: 600,
					}}
				>
					Invitation
				</span>
				Vous rejoignez <strong>{inspection.organizationName}</strong> en tant
				que <strong>{RoleLabelsInline[inspection.role]}</strong>. Aucune
				organisation ne sera créée à votre nom.
			</div>
		)
	}
	const message =
		inspection.status === 'not_found'
			? "Ce lien d'invitation n'est pas valide. Vous pouvez tout de même créer un compte ci-dessous."
			: inspection.status === 'revoked'
				? 'Cette invitation a été révoquée. Vous pouvez tout de même créer un compte ci-dessous.'
				: inspection.status === 'expired'
					? 'Cette invitation a expiré. Vous pouvez tout de même créer un compte ci-dessous.'
					: 'Cette invitation a déjà été utilisée. Vous pouvez tout de même créer un compte ci-dessous.'
	return (
		<div
			className="mb-6 rounded-lg p-3.5 text-sm"
			style={{
				background: 'rgba(216, 138, 29, 0.08)',
				border: '1px solid rgba(216, 138, 29, 0.25)',
				color: '#7A4F12',
			}}
		>
			{message}
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
